import React, { type ComponentPropsWithRef } from "react";
import { NumericFormat } from "react-number-format";
import { InvoiceDueDateSubsegment } from "@common/types/invoice";
import {
    findInvoiceDueDateBucket,
    getInvoiceDueDateBucketFilters,
} from "@components/invoices/invoiceDueDateBuckets";
import { Field } from "@design-system/Field/Field";
import { HStack } from "@design-system/HStack/HStack";
import { Input } from "@design-system/Input/Input";
import { Picker } from "@design-system/Picker/Picker.lib";
import { VStack } from "@design-system/VStack/VStack";
import { BaseFilterProps } from "../../lib";
import styles from "./OverDueDateFilter.module.scss";

const ANY_OPTION = "any";
const OVERDUE_OPTION = "overdue";

type DueDateOption =
    | typeof ANY_OPTION
    | typeof OVERDUE_OPTION
    | InvoiceDueDateSubsegment;

const dueDateItems: Array<{ value: DueDateOption; label: string }> = [
    { value: ANY_OPTION, label: "Any" },
    { value: InvoiceDueDateSubsegment.CURRENT, label: "Not due yet" },
    { value: OVERDUE_OPTION, label: "Overdue" },
    {
        value: InvoiceDueDateSubsegment.PAST_DUE_1_TO_30,
        label: "1–30 days overdue",
    },
    {
        value: InvoiceDueDateSubsegment.PAST_DUE_31_TO_60,
        label: "31–60 days overdue",
    },
    {
        value: InvoiceDueDateSubsegment.PAST_DUE_61_TO_90,
        label: "61–90 days overdue",
    },
    {
        value: InvoiceDueDateSubsegment.PAST_DUE_91_PLUS,
        label: "91+ days overdue",
    },
];

const isDueDateOption = (value: string | number): value is DueDateOption =>
    dueDateItems.some((item) => item.value === value);

const OverdueDaysInput = (
    props: Omit<ComponentPropsWithRef<"input">, "size">,
) => <Input {...props} size="xl" clearable={false} />;

const getSelectedOption = ({
    overdue,
    minOverdue,
    maxOverdue,
}: BaseFilterProps["filters"]): DueDateOption => {
    if (overdue === undefined) {
        return ANY_OPTION;
    }
    if (!overdue) {
        return InvoiceDueDateSubsegment.CURRENT;
    }
    return (
        findInvoiceDueDateBucket({ overdue, minOverdue, maxOverdue }) ??
        OVERDUE_OPTION
    );
};

export const OverDueDateFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const selectedOption = getSelectedOption(filters);

    const handleOptionChange = (value: string | number | null) => {
        if (value === null || !isDueDateOption(value)) {
            return;
        }
        if (value === ANY_OPTION) {
            onChange({
                overdue: undefined,
                minOverdue: undefined,
                maxOverdue: undefined,
            });
        } else if (value === OVERDUE_OPTION) {
            onChange({
                overdue: true,
                minOverdue: undefined,
                maxOverdue: undefined,
            });
        } else {
            onChange(getInvoiceDueDateBucketFilters(value));
        }
    };

    return (
        <VStack gap={16}>
            <Field label="Due date" control="button">
                <Picker.Select
                    items={dueDateItems}
                    value={selectedOption}
                    onValueChange={handleOptionChange}
                    trigger={
                        <Picker.Button variant="primary" size="xl" block />
                    }
                />
            </Field>

            {filters.overdue === true && (
                <HStack gap={16}>
                    <Field label="From" className={styles.valueField}>
                        <NumericFormat
                            value={filters.minOverdue ?? ""}
                            onValueChange={(values, { source }) => {
                                if (source === "event") {
                                    onChange({
                                        minOverdue: values.floatValue,
                                    });
                                }
                            }}
                            suffix=" days"
                            customInput={OverdueDaysInput}
                            allowNegative={false}
                            decimalScale={0}
                        />
                    </Field>

                    <Field label="To" className={styles.valueField}>
                        <NumericFormat
                            value={filters.maxOverdue ?? ""}
                            onValueChange={(values, { source }) => {
                                if (source === "event") {
                                    onChange({
                                        maxOverdue: values.floatValue,
                                    });
                                }
                            }}
                            suffix=" days"
                            customInput={OverdueDaysInput}
                            allowNegative={false}
                            decimalScale={0}
                        />
                    </Field>
                </HStack>
            )}
        </VStack>
    );
};
